import { useTranslation } from 'react-i18next'

import { type CompradorResumo } from '@/features/proposta/entities/ResumoProposta/resumo-compradores-proposta'
import { InfoItem } from '@/pages/proposta/resumo-proposta/components/info-item'
import { Icon } from '@/shared/components/icon'
import { Button, Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui'

type TCardCompradoresProps = {
  onEdit: (step: number) => void
  resumo: {
    readonly titular: CompradorResumo
    readonly compradores: readonly CompradorResumo[]
  }
}

const STEP_CADASTRO_COMPRADORES = 1

export function CardCompradores({ resumo, onEdit }: Readonly<TCardCompradoresProps>) {
  const { t } = useTranslation('proposta')
  return (
    <Card className="shadow-none">
      <CardHeader className="bg-neutral-200 py-3">
        <div className="md:0 flex flex-col justify-between gap-3 md:flex-row md:items-center">
          <CardTitle className="text-base font-normal text-neutral-900">
            {t('resumo_proposta.compradores.titulo')}
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            className="text-primary border-primary border-1 hover:bg-neutral-200 md:border-none"
            onClick={() => onEdit(STEP_CADASTRO_COMPRADORES)}
          >
            <Icon name="pencil" className="mr-1 h-4 w-4" />
            {t('resumo_proposta.editar')}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        <BlocoComprador
          titulo={t('resumo_proposta.compradores.label.titular')}
          nome={resumo.titular.nome}
          tipoDoc={resumo.titular.tipoDocumento}
          documento={resumo.titular.documento}
          participacao={resumo.titular.participacao}
          semBordaSuperior
        />
        {resumo.compradores.map((c) => (
          <BlocoComprador
            key={c.nome}
            titulo={t('resumo_proposta.compradores.label.comprador')}
            nome={c.nome}
            tipoDoc={c.tipoDocumento}
            documento={c.documento}
            participacao={c.participacao}
          />
        ))}
      </CardContent>
    </Card>
  )
}

type TBlocoCompradorProps = Readonly<{
  titulo: string
  nome: string
  tipoDoc: string
  documento: string
  participacao: string
  semBordaSuperior?: boolean
}>

function BlocoComprador({ titulo, nome, tipoDoc, documento, participacao, semBordaSuperior }: TBlocoCompradorProps) {
  const { t } = useTranslation('proposta')
  return (
    <div data-testid={documento} className={!semBordaSuperior ? 'border-t border-neutral-300' : ''}>
      <InfoItem rotulo={titulo} valor={nome} bordaInferior className="space-y-1 border-neutral-100 py-2" />
      <div className="grid grid-cols-2 gap-4 py-2">
        <InfoItem rotulo={tipoDoc} valor={documento} bordaDireita className="border-gray-100" />
        <InfoItem rotulo={t('resumo_proposta.compradores.label.participacao')} valor={participacao} />
      </div>
    </div>
  )
}
